import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import api from '../services/api';
import { takeStoredCodeVerifier, removeStoredCodeVerifier } from '../utils/pkce';

const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;

/**
 * Callback page for Microsoft OAuth when accepting an invitation.
 * Azure redirects here with ?code=...&state=<invite_token>.
 * We exchange the code in the browser (SPA PKCE flow), then send the id_token to the backend.
 */
const InviteCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const token = searchParams.get('state');
    if (!code || !token) {
      setError('Missing code or invitation token. Please try the invitation link again.');
      return;
    }
    if (handledRef.current) return;
    handledRef.current = true;
    const codeVerifier = takeStoredCodeVerifier();
    if (!codeVerifier) {
      setError('Sign-in session expired. Please try the invitation link again.');
      return;
    }

    const redirectUri = `${window.location.origin}/invite/callback`;

    (async () => {
      try {
        const tokenResponse = await fetch(
          'https://login.microsoftonline.com/common/oauth2/v2.0/token',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              client_id: AZURE_CLIENT_ID!,
              grant_type: 'authorization_code',
              code,
              redirect_uri: redirectUri,
              scope: 'openid email profile',
              code_verifier: codeVerifier,
            }),
          },
        );
        const tokenData = await tokenResponse.json();
        if (!tokenResponse.ok || !tokenData.id_token) {
          throw new Error(tokenData.error_description || 'Microsoft token exchange failed');
        }
        removeStoredCodeVerifier();
        const formData = new URLSearchParams();
        formData.append('microsoft_id_token', tokenData.id_token);
        await api.post(`/auth/invite/${token}/accept`, formData.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        navigate('/login', { state: { message: 'Invitation accepted! You can now log in.' } });
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to accept invitation');
      }
    })();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Paper sx={{ p: 4, minWidth: 350, textAlign: 'center' }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Typography variant="body2" sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={() => navigate('/login')}>
              Back to login
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography>Completing sign-in...</Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InviteCallback;
